import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Save, RefreshCw, DollarSign, Truck, Clock, Target } from 'lucide-react';

export const Settings = () => {
  const [settings, setSettings] = useState({
    eur_usd_rate: 1.10,
    shipping_rate_per_kg_eur: 3.75,
    supplier_lead_time_days: 14,
    prep_time_days: 3,
    shipping_time_days: 7,
    target_stock_min_days: 30,
    target_stock_max_days: 60,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        eur_usd_rate: parseFloat(settings.eur_usd_rate),
        shipping_rate_per_kg_eur: parseFloat(settings.shipping_rate_per_kg_eur),
        supplier_lead_time_days: parseInt(settings.supplier_lead_time_days),
        prep_time_days: parseInt(settings.prep_time_days),
        shipping_time_days: parseInt(settings.shipping_time_days),
        target_stock_min_days: parseInt(settings.target_stock_min_days),
        target_stock_max_days: parseInt(settings.target_stock_max_days),
      };
      const response = await settingsAPI.update(dataToSave);
      setSettings(response.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const totalLeadTime = 
    parseInt(settings.supplier_lead_time_days || 0) + 
    parseInt(settings.prep_time_days || 0) + 
    parseInt(settings.shipping_time_days || 0);

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="settings-title">Settings</h1>
          <p className="page-subtitle">Configure rates and planner parameters</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} disabled={loading} data-testid="refresh-settings-btn">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Currency & Shipping Rates */}
        <Card data-testid="rates-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign size={20} />
              Currency & Shipping Rates
            </CardTitle>
            <CardDescription>
              Configure exchange rates and shipping costs for COGS calculation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="eur_usd_rate">EUR/USD Exchange Rate</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-slate-500">1 EUR =</span>
                <Input
                  id="eur_usd_rate"
                  type="number"
                  step="0.01"
                  value={settings.eur_usd_rate}
                  onChange={(e) => updateSetting('eur_usd_rate', e.target.value)}
                  className="w-32"
                  data-testid="eur-usd-rate-input"
                />
                <span className="text-slate-500">USD</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Used for converting EUR costs to USD in COGS calculation
              </p>
            </div>

            <Separator />

            <div>
              <Label htmlFor="shipping_rate">Shipping Rate (per kg)</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-slate-500">€</span>
                <Input
                  id="shipping_rate"
                  type="number"
                  step="0.01"
                  value={settings.shipping_rate_per_kg_eur}
                  onChange={(e) => updateSetting('shipping_rate_per_kg_eur', e.target.value)}
                  className="w-32"
                  data-testid="shipping-rate-input"
                />
                <span className="text-slate-500">EUR/kg</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Standard shipping rate from supplier to Amazon FBA
              </p>
            </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium mb-2">COGS Formula:</p>
                <code className="text-xs bg-white p-2 rounded block">
                  COGS = (Product Cost € + (Weight g × €{settings.shipping_rate_per_kg_eur}/kg / 1000)) × {settings.eur_usd_rate}
                </code>
              </div>
          </CardContent>
        </Card>

        {/* Lead Time Settings */}
        <Card data-testid="leadtime-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock size={20} />
              Lead Time Configuration
            </CardTitle>
            <CardDescription>
              Set the time parameters for replenishment planning
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="supplier_lead_time">Supplier Lead Time</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="supplier_lead_time"
                    type="number"
                    value={settings.supplier_lead_time_days}
                    onChange={(e) => updateSetting('supplier_lead_time_days', e.target.value)}
                    className="w-20"
                    data-testid="supplier-leadtime-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
              </div>
              <div>
                <Label htmlFor="prep_time">Prep Time</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="prep_time"
                    type="number"
                    value={settings.prep_time_days}
                    onChange={(e) => updateSetting('prep_time_days', e.target.value)}
                    className="w-20"
                    data-testid="prep-time-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
              </div>
              <div>
                <Label htmlFor="shipping_time">Shipping Time</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="shipping_time"
                    type="number"
                    value={settings.shipping_time_days}
                    onChange={(e) => updateSetting('shipping_time_days', e.target.value)}
                    className="w-20"
                    data-testid="shipping-time-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
              </div>
              <div className="flex items-end">
                <div className="p-3 bg-blue-50 rounded-lg w-full">
                  <p className="text-xs text-blue-600">Total Lead Time</p>
                  <p className="text-xl font-bold text-blue-800">{totalLeadTime} days</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Target Stock Settings */}
        <Card data-testid="target-stock-card" className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={20} />
              Target Stock Range
            </CardTitle>
            <CardDescription>
              Define the target inventory levels in days of stock
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="min_stock">Minimum Stock Target</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="min_stock"
                    type="number"
                    value={settings.target_stock_min_days}
                    onChange={(e) => updateSetting('target_stock_min_days', e.target.value)}
                    className="w-24"
                    data-testid="min-stock-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Stock below this triggers "Medium" urgency
                </p>
              </div>
              <div>
                <Label htmlFor="max_stock">Maximum Stock Target</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="max_stock"
                    type="number"
                    value={settings.target_stock_max_days}
                    onChange={(e) => updateSetting('target_stock_max_days', e.target.value)}
                    className="w-24"
                    data-testid="max-stock-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Optimal stock level to maintain
                </p>
              </div>
              <div className="flex items-end">
                <div className="p-4 bg-slate-50 rounded-lg w-full">
                  <p className="text-sm font-medium mb-2">Urgency Levels:</p>
                  <div className="space-y-1 text-xs">
                    <p><span className="badge urgency-critical">Critical</span> &lt; {totalLeadTime} days</p>
                    <p><span className="badge urgency-high">High</span> &lt; {settings.target_stock_min_days} days</p>
                    <p><span className="badge urgency-medium">Medium</span> &lt; {settings.target_stock_max_days} days</p>
                    <p><span className="badge urgency-low">Low</span> ≥ {settings.target_stock_max_days} days</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
